import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  CircularProgress,
  Stack
} from '@mui/material';
import SecretarySidebar from '../../components/SecretarySidebar';
import {
  School as SchoolIcon,
  Class as ClassIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Event as EventIcon
} from '@mui/icons-material';
import axios from 'axios';

interface Level {
  id: number;
  name: string;
}

interface Class {
  id: number;
  name: string;
  level_id: number;
}

interface Student {
  id: number;
  first_name: string;
  last_name: string;
  matricule: string;
}

interface Absence {
  id: number;
  date: string;
  reason: string;
  status: 'excused' | 'unexcused';
  duration_hours: number;
  semester: string;
  subject_name: string;
  teacher_first_name: string;
  teacher_last_name: string;
}

const JustificationAbsences = () => {
  const [currentView, setCurrentView] = useState<'levels' | 'classes' | 'students'>('levels');
  const [levels, setLevels] = useState<Level[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<Level | null>(null);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });
  const [justificationDialog, setJustificationDialog] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState<Absence | null>(null);
  const [justificationReason, setJustificationReason] = useState('');
  const [schoolYear, setSchoolYear] = useState('2024-2025');
  const [availableSchoolYears, setAvailableSchoolYears] = useState<string[]>([]);

  // Charger les années scolaires disponibles
  const fetchSchoolYears = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('https://trayebernard-college.com/api/school-years', {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Extraire les noms des années scolaires
      const schoolYearNames = data.map((year: any) => year.name);
      
      // S'assurer que 2025-2026 est toujours dans la liste
      if (!schoolYearNames.includes('2025-2026')) {
        schoolYearNames.unshift('2025-2026');
      }
      
      setAvailableSchoolYears(schoolYearNames);
    } catch (err: any) {
      console.error('Erreur lors du chargement des années scolaires:', err);
      // Fallback: générer les années scolaires avec 2025-2026 en premier
      const years = ['2025-2026'];
      const currentYear = new Date().getFullYear();
      for (let i = 0; i < 5; i++) {
        const year = `${currentYear - i}-${currentYear - i + 1}`;
        if (year !== '2025-2026') {
          years.push(year);
        }
      }
      setAvailableSchoolYears(years);
    }
  };

  // Charger les niveaux
  const fetchLevels = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get('https://trayebernard-college.com/api/levels', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLevels(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des niveaux');
    } finally {
      setLoading(false);
    }
  };

  // Charger les classes d'un niveau (sans filtrage par année scolaire)
  const fetchClasses = async (levelId: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`https://trayebernard-college.com/api/levels/${levelId}/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des classes');
    } finally {
      setLoading(false);
    }
  };

  // Charger les élèves d'une classe avec une année scolaire spécifique
  const fetchStudentsWithYear = async (classId: number, year: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = `https://trayebernard-college.com/api/classes/${classId}/students?school_year=${year}`;
      
      console.log('=== DEBUG fetchStudentsWithYear ===');
      console.log('classId:', classId);
      console.log('year:', year);
      console.log('URL appelée:', url);
      
      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Données reçues:', data);
      console.log('Nombre d\'élèves:', data.length);
      
      setStudents(data);
    } catch (err: any) {
      console.error('Erreur fetchStudentsWithYear:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des élèves');
    } finally {
      setLoading(false);
    }
  };

  // Charger les élèves d'une classe
  const fetchStudents = async (classId: number) => {
    return fetchStudentsWithYear(classId, schoolYear);
  };

  // Charger les absences d'un élève
  const fetchStudentAbsences = async (studentId: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`https://trayebernard-college.com/api/absences?student_id=${studentId}&school_year=${schoolYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAbsences(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des absences');
    } finally {
      setLoading(false);
    }
  };

  // Justifier une absence
  const justifyAbsence = async () => {
    if (!selectedAbsence || !justificationReason.trim()) {
      setSnackbar({ open: true, message: 'Veuillez saisir une raison de justification', severity: 'error' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`https://trayebernard-college.com/api/absences/${selectedAbsence.id}`, {
        status: 'excused',
        reason: justificationReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSnackbar({ open: true, message: 'Absence justifiée avec succès', severity: 'success' });
      setJustificationDialog(false);
      setJustificationReason('');
      setSelectedAbsence(null);
      
      // Recharger les absences
      if (selectedStudent) {
        await fetchStudentAbsences(selectedStudent.id);
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erreur lors de la justification', severity: 'error' });
    }
  };

  // Annuler une justification
  const unJustifyAbsence = async (absenceId: number) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`https://trayebernard-college.com/api/absences/${absenceId}`, {
        status: 'unexcused',
        reason: 'Absence'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSnackbar({ open: true, message: 'Justification annulée avec succès', severity: 'success' });
      
      // Recharger les absences
      if (selectedStudent) {
        await fetchStudentAbsences(selectedStudent.id);
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erreur lors de l\'annulation', severity: 'error' });
    }
  };

  const handleLevelClick = async (level: Level) => {
    setSelectedLevel(level);
    setCurrentView('classes');
    await fetchClasses(level.id);
  };

  const handleClassClick = async (classe: Class) => {
    setSelectedClass(classe);
    setCurrentView('students');
    await fetchStudents(classe.id);
  };

  const handleStudentClick = async (student: Student) => {
    setSelectedStudent(student);
    await fetchStudentAbsences(student.id);
  };

  const goBack = () => {
    if (currentView === 'classes') {
      setCurrentView('levels');
      setSelectedLevel(null);
      setClasses([]);
    } else if (currentView === 'students') {
      setCurrentView('classes');
      setSelectedClass(null);
      setStudents([]);
      setSelectedStudent(null);
      setAbsences([]);
    }
  };

  const handleSchoolYearChange = (newSchoolYear: string) => {
    console.log('=== DEBUG handleSchoolYearChange ===');
    console.log('Ancienne année scolaire:', schoolYear);
    console.log('Nouvelle année scolaire:', newSchoolYear);
    console.log('currentView:', currentView);
    console.log('selectedClass:', selectedClass);
    
    setSchoolYear(newSchoolYear);
    // Réinitialiser seulement les données des élèves et absences
    setSelectedStudent(null);
    setStudents([]);
    setAbsences([]);
    setError(null);
    
    // Si on est dans la vue des étudiants, recharger les étudiants de la classe sélectionnée
    if (currentView === 'students' && selectedClass) {
      console.log('Rechargement des étudiants pour la classe:', selectedClass.id, 'avec l\'année:', newSchoolYear);
      // Appeler fetchStudents avec la nouvelle année directement
      fetchStudentsWithYear(selectedClass.id, newSchoolYear);
    }
  };

  useEffect(() => {
    fetchSchoolYears();
    fetchLevels();
  }, []);

  const renderLevels = () => (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: 'primary.main' }}>
        <EventIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
        Justifications d'absences
      </Typography>
      
      <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
        Sélectionnez un niveau :
      </Typography>

      <Grid container spacing={3}>
        {levels.map((level) => (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={level.id}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => handleLevelClick(level)}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <SchoolIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {level.name}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderClasses = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={goBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
          Classes - {selectedLevel?.name}
        </Typography>
      </Box>

      <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
        Sélectionnez une classe :
      </Typography>

      <Grid container spacing={3}>
        {classes.map((classe) => (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={classe.id}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => handleClassClick(classe)}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <ClassIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {classe.name}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderStudents = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={goBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
          Élèves - {selectedClass?.name}
        </Typography>
      </Box>

      {/* Sélecteur d'année scolaire pour filtrer les élèves */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#e3f2fd', border: '1px solid #2196f3' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
            Année scolaire (filtre les élèves) :
          </Typography>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Année</InputLabel>
            <Select
              value={schoolYear}
              onChange={(e) => handleSchoolYearChange(e.target.value)}
              label="Année"
            >
              {availableSchoolYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
        Sélectionnez un élève pour gérer ses justifications :
      </Typography>

      <Grid container spacing={3}>
        {students.map((student) => (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={student.id}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => handleStudentClick(student)}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <PeopleIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {student.first_name} {student.last_name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Matricule: {student.matricule}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {selectedStudent && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Absences de {selectedStudent.first_name} {selectedStudent.last_name}
          </Typography>
          
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Matière</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Professeur</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Durée (h)</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Statut</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {absences.map((absence) => (
                  <TableRow key={absence.id}>
                    <TableCell>{new Date(absence.date).toLocaleDateString('fr-FR')}</TableCell>
                    <TableCell>{absence.subject_name}</TableCell>
                    <TableCell>{absence.teacher_first_name} {absence.teacher_last_name}</TableCell>
                    <TableCell>{absence.duration_hours}</TableCell>
                    <TableCell>
                      <Chip
                        label={absence.status === 'excused' ? 'Justifiée' : 'Non justifiée'}
                        color={absence.status === 'excused' ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {absence.status === 'unexcused' ? (
                        <Tooltip title="Justifier l'absence">
                          <IconButton
                            color="success"
                            onClick={() => {
                              setSelectedAbsence(absence);
                              setJustificationDialog(true);
                            }}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Annuler la justification">
                          <IconButton
                            color="error"
                            onClick={() => unJustifyAbsence(absence.id)}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <SecretarySidebar />
      <Box sx={{ 
        flexGrow: 1, 
        marginLeft: '0px',
        padding: 3,
        width: 'calc(100% - 250px)',
        minHeight: '100vh',
        overflow: 'auto',
        boxSizing: 'border-box',
        position: 'absolute',
        left: '250px',
        right: 0
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {currentView === 'levels' && renderLevels()}
        {currentView === 'classes' && renderClasses()}
        {currentView === 'students' && renderStudents()}

        {/* Dialog pour justifier une absence */}
        <Dialog open={justificationDialog} onClose={() => setJustificationDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Justifier l'absence</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Raison de la justification"
              value={justificationReason}
              onChange={(e) => setJustificationReason(e.target.value)}
              sx={{ mt: 2 }}
              placeholder="Expliquez la raison de l'absence..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setJustificationDialog(false)}>Annuler</Button>
            <Button onClick={justifyAbsence} variant="contained" color="success">
              Justifier
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default JustificationAbsences;
